import type { 
  ApiResponse, 
  SuperteamsResponse, 
  CountriesResponse,
  MembersResponse, 
  AuthResponse, 
  User,
  UserRole,
  AdminUserResult,
  AdminReviewProjectsResponse,
  AdminReviewProject,
  AdminCompanyProfileImportsResponse,
  AdminCompanyProfileDetailResponse,
  CompanyProfileReviewStatus,
  Country,
  BuilderTeam,
  TeamsResponse,
  TeamMembersResponse,
  TeamInvitation,
  InvitationsResponse,
  Project,
  ProjectsResponse,
  ProjectMedia,
  MediaType,
  PublicStats,
  PublicMembersResponse,
  PublicTeamsResponse,
  PublicProjectsResponse,
  Achievement,
  AchievementsResponse,
  AchievementType,
  SuperteamLeaderboardResponse,
  CountryLeaderboardResponse,
  GlobalLeaderboardResponse,
} from '../types';

// Remove trailing slash and ensure /api prefix
const rawUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '');
const API_BASE_URL = rawUrl.endsWith('/api') ? rawUrl : `${rawUrl}/api`;

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  // Debug: Log token status for verify endpoints
  if (endpoint.includes('verify')) {
    console.log('[fetchApi] Endpoint:', endpoint);
    console.log('[fetchApi] Token exists:', !!token);
    console.log('[fetchApi] Token value:', token ? token.substring(0, 30) + '...' : 'NULL');
  }
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options?.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' })) as {
      error?: string;
      hint?: string;
      message?: string;
    };
    const base = error.error || error.message || `HTTP ${response.status}`;
    const errorMessage = error.hint ? `${base} ${error.hint}` : base;
    throw new Error(errorMessage);
  }

  return response.json();
}

// Superteam endpoints
export async function getSuperteams(): Promise<SuperteamsResponse> {
  return fetchApi<SuperteamsResponse>('/superteams');
}

export async function getSuperteamById(id: string): Promise<ApiResponse<{ superteam: any }>> {
  return fetchApi(`/superteams/${id}`);
}

export async function getSuperteamMembers(superteamId: string): Promise<MembersResponse> {
  return fetchApi<MembersResponse>(`/superteams/${superteamId}/members`);
}

export async function getGlobalStats(): Promise<ApiResponse<{ totalBuilders: number }>> {
  return fetchApi('/superteams/stats');
}

// Auth endpoints
export async function getAuthUrl(): Promise<{ url: string }> {
  return fetchApi<{ url: string }>('/auth/x/url');
}

export async function handleAuthCallback(code: string, state: string): Promise<AuthResponse> {
  return fetchApi<AuthResponse>('/auth/x/callback', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  });
}

export async function getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
  return fetchApi('/auth/me');
}

// Search users by username or display name
export async function searchUsers(query: string, limit?: number): Promise<ApiResponse<{
  users: Array<{
    id: string;
    xUsername: string;
    xDisplayName: string;
    xAvatarUrl: string;
    isVerified: boolean;
  }>;
}>> {
  const params = new URLSearchParams({ q: query });
  if (limit) params.set('limit', String(limit));
  return fetchApi(`/users/search?${params.toString()}`);
}

export async function logout(): Promise<void> {
  localStorage.removeItem('auth_token');
}

// Profile endpoints
export async function updateProfile(data: Partial<User>): Promise<ApiResponse<{ user: User }>> {
  return fetchApi('/users/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function joinSuperteam(superteamId: string): Promise<ApiResponse<{ user: User }>> {
  return fetchApi('/users/superteam', {
    method: 'POST',
    body: JSON.stringify({ superteamId }),
  });
}

export async function leaveSuperteam(): Promise<ApiResponse<{ user: User }>> {
  return fetchApi('/users/superteam', {
    method: 'DELETE',
  });
}

// Verification endpoints (Lead/Lieutenant only)
export async function verifyMember(userId: string): Promise<ApiResponse<{ user: User }>> {
  return fetchApi(`/users/${userId}/verify`, {
    method: 'POST',
  });
}

export async function unverifyMember(userId: string): Promise<ApiResponse<{ user: User }>> {
  return fetchApi(`/users/${userId}/unverify`, {
    method: 'POST',
  });
}

// Role management endpoints (Lead only)
export async function updateUserRole(userId: string, role: UserRole): Promise<ApiResponse<{ user: User }>> {
  return fetchApi(`/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

// Admin endpoints
export async function adminSearchUsers(query: string): Promise<ApiResponse<{ users: AdminUserResult[] }>> {
  return fetchApi(`/admin/users/search?q=${encodeURIComponent(query)}`);
}

export async function adminSetUserRole(
  userId: string, 
  role: UserRole, 
  superteamId?: string
): Promise<ApiResponse<{ user: AdminUserResult }>> {
  return fetchApi(`/admin/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role, superteamId }),
  });
}

export async function adminGetLeads(): Promise<ApiResponse<{ leads: (AdminUserResult & { superteamName?: string })[] }>> {
  return fetchApi('/admin/leads');
}

export async function adminGetReviewProjects(
  status: 'pending_review' | 'needs_more_data' | 'approved' | 'all' = 'pending_review'
): Promise<ApiResponse<AdminReviewProjectsResponse>> {
  return fetchApi(`/admin/projects/review?status=${encodeURIComponent(status)}`);
}

export async function adminReviewProject(
  projectId: string,
  action: 'approve' | 'needs_more_data'
): Promise<ApiResponse<{ project: Pick<AdminReviewProject, 'id' | 'status' | 'reviewStatus'> }>> {
  return fetchApi(`/admin/projects/${projectId}/review`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
}

export async function adminGetReviewProjectById(
  projectId: string
): Promise<ApiResponse<{ project: Project }>> {
  return fetchApi(`/admin/projects/${projectId}/review`);
}

export async function adminDeleteReviewProject(
  projectId: string
): Promise<ApiResponse<{ project: Pick<AdminReviewProject, 'id'> }>> {
  return fetchApi(`/admin/projects/${projectId}`, {
    method: 'DELETE',
  });
}

export async function adminGetCompanyProfileImports(
  status: CompanyProfileReviewStatus | 'all' = 'pending_review',
): Promise<ApiResponse<AdminCompanyProfileImportsResponse>> {
  return fetchApi(`/admin/company-profiles/review?status=${encodeURIComponent(status)}`);
}

export async function adminGetCompanyProfileImportById(
  id: string,
): Promise<ApiResponse<AdminCompanyProfileDetailResponse>> {
  return fetchApi(`/admin/company-profiles/${id}/review`);
}

export async function adminReviewCompanyProfileImport(
  id: string,
  action: 'approve' | 'reject',
): Promise<
  ApiResponse<{
    company: { id: string; xUsername: string; reviewStatus: string; reviewedAt: string | null };
  }>
> {
  return fetchApi(`/admin/company-profiles/${id}/review`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
}

export async function adminBulkVerifyUsers(
  usernames: string[],
  superteamId?: string
): Promise<ApiResponse<{
  verified: number;
  verifiedUsers: Array<{ id: string; xUsername: string; xDisplayName: string; isVerified: boolean; superteamId: string | null }>;
  notFound: string[];
  message: string;
}>> {
  return fetchApi('/admin/users/bulk-verify', {
    method: 'POST',
    body: JSON.stringify({ usernames, ...(superteamId && { superteamId }) }),
  });
}

// Country endpoints
export async function getCountries(): Promise<CountriesResponse> {
  return fetchApi<CountriesResponse>('/countries');
}

export async function getCountryByCode(code: string): Promise<ApiResponse<{ country: Country }>> {
  return fetchApi(`/countries/${code}`);
}

export async function getCountryMembers(countryCode: string): Promise<MembersResponse> {
  return fetchApi<MembersResponse>(`/countries/${countryCode}/members`);
}

export async function joinCountry(countryCode: string): Promise<ApiResponse<{ user: User }>> {
  return fetchApi('/users/country', {
    method: 'POST',
    body: JSON.stringify({ countryCode }),
  });
}

export async function leaveCountry(): Promise<ApiResponse<{ user: User }>> {
  return fetchApi('/users/country', {
    method: 'DELETE',
  });
}

// ==================== Builder Teams API ====================

// Get all teams the current user is a member of
export async function getMyTeams(): Promise<ApiResponse<TeamsResponse>> {
  return { success: true, data: { teams: [] } };
}

// Create a new team
export async function createTeam(data: {
  name: string;
  description?: string;
  logoUrl?: string;
}): Promise<ApiResponse<{ team: BuilderTeam }>> {
  void data;
  throw new Error('Teams are removed. Use project creation directly.');
}

// Get team by ID
export async function getTeamById(id: string): Promise<ApiResponse<{ team: BuilderTeam }>> {
  void id;
  throw new Error('Teams are removed.');
}

// Update team
export async function updateTeam(
  id: string,
  data: { name?: string; description?: string; logoUrl?: string }
): Promise<ApiResponse<{ team: BuilderTeam }>> {
  void id;
  void data;
  throw new Error('Teams are removed.');
}

// Delete team
export async function deleteTeam(id: string): Promise<ApiResponse<void>> {
  void id;
  throw new Error('Teams are removed.');
}

// Get team members
export async function getTeamMembers(teamId: string): Promise<ApiResponse<TeamMembersResponse>> {
  void teamId;
  return { success: true, data: { members: [] } };
}

// Send team invitation
export async function sendTeamInvitation(
  teamId: string,
  xUsername: string
): Promise<ApiResponse<{ invitation: TeamInvitation }>> {
  void teamId;
  void xUsername;
  throw new Error('Teams are removed.');
}

// Get pending invitations for a team
export async function getTeamInvitations(teamId: string): Promise<ApiResponse<InvitationsResponse>> {
  void teamId;
  return { success: true, data: { invitations: [] } };
}

// Get my pending invitations
export async function getMyInvitations(): Promise<ApiResponse<InvitationsResponse>> {
  return { success: true, data: { invitations: [] } };
}

// Accept invitation
export async function acceptInvitation(invitationId: string): Promise<ApiResponse<void>> {
  void invitationId;
  throw new Error('Teams are removed.');
}

// Decline invitation
export async function declineInvitation(invitationId: string): Promise<ApiResponse<void>> {
  void invitationId;
  throw new Error('Teams are removed.');
}

// Remove team member
export async function removeTeamMember(teamId: string, memberId: string): Promise<ApiResponse<void>> {
  void teamId;
  void memberId;
  throw new Error('Teams are removed.');
}

// Update team member role
export async function updateTeamMemberRole(
  teamId: string,
  memberId: string,
  role: 'admin' | 'member'
): Promise<ApiResponse<void>> {
  void teamId;
  void memberId;
  void role;
  throw new Error('Teams are removed.');
}

// ==================== Projects API ====================

// Get projects (public or filtered)
export async function getProjects(options?: {
  status?: 'draft' | 'published' | 'all';
  teamId?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<ProjectsResponse>> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.teamId) params.set('teamId', options.teamId);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  
  const query = params.toString();
  return fetchApi(`/projects${query ? `?${query}` : ''}`);
}

// Create a new project
export async function createProject(data: {
  teamId?: string;
  name: string;
  tagline?: string;
  description?: string;
  paperContent?: string;
  website?: string;
  github?: string;
  twitter?: string;
  discord?: string;
}): Promise<ApiResponse<{ project: Project }>> {
  return fetchApi('/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Get project by ID
export async function getProjectById(id: string): Promise<ApiResponse<{ project: Project }>> {
  return fetchApi(`/projects/${id}`);
}

// Update project
export async function updateProject(
  id: string,
  data: {
    name?: string;
    tagline?: string;
    description?: string;
    paperContent?: string;
    website?: string;
    github?: string;
    twitter?: string;
    discord?: string;
  }
): Promise<ApiResponse<{ project: Project }>> {
  return fetchApi(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Delete project
export async function deleteProject(id: string): Promise<ApiResponse<void>> {
  return fetchApi(`/projects/${id}`, {
    method: 'DELETE',
  });
}

// Publish project
export async function publishProject(id: string): Promise<ApiResponse<{ project: Project }>> {
  return fetchApi(`/projects/${id}/publish`, {
    method: 'POST',
  });
}

// Unpublish project
export async function unpublishProject(id: string): Promise<ApiResponse<{ project: Project }>> {
  return fetchApi(`/projects/${id}/unpublish`, {
    method: 'POST',
  });
}

// Update project members
export async function updateProjectMembers(
  projectId: string,
  members: Array<{ teamMemberId: string; role: 'lead' | 'developer' | 'designer' | 'marketer' | 'researcher' | 'contributor' | 'advisor' }>
): Promise<ApiResponse<{ project: Project }>> {
  void members;
  return fetchApi(`/projects/${projectId}`);
}

// Add media to project
export async function addProjectMedia(
  projectId: string,
  data: {
    mediaType: MediaType;
    category: 'logo' | 'banner' | 'media';
    url: string;
    cloudflareKey?: string;
    thumbnailUrl?: string;
  }
): Promise<ApiResponse<{ media: ProjectMedia }>> {
  return fetchApi(`/projects/${projectId}/media`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Reorder project media
export async function reorderProjectMedia(
  projectId: string,
  mediaIds: string[]
): Promise<ApiResponse<void>> {
  return fetchApi(`/projects/${projectId}/media/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ mediaIds }),
  });
}

// Delete project media
export async function deleteProjectMedia(
  projectId: string,
  mediaId: string
): Promise<ApiResponse<void>> {
  return fetchApi(`/projects/${projectId}/media/${mediaId}`, {
    method: 'DELETE',
  });
}

// Verify project X account
export async function verifyProjectXAccount(
  projectId: string,
  xUsername: string
): Promise<ApiResponse<{ project: Project }>> {
  return fetchApi(`/projects/${projectId}/verify-x`, {
    method: 'POST',
    body: JSON.stringify({ xUsername }),
  });
}

// ==================== Media Upload API ====================

// Check if media uploads are available
export async function getMediaStatus(): Promise<ApiResponse<{ configured: boolean }>> {
  return fetchApi('/media/status');
}

// Upload file through backend (all uploads go through backend to avoid CORS)
export async function uploadFile(
  file: File,
  folder: 'projects' | 'teams' = 'projects'
): Promise<ApiResponse<{ url: string; key: string; mediaType: MediaType }>> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  const response = await fetch(`${API_BASE_URL}/media/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' })) as {
      error?: string;
      hint?: string;
      message?: string;
    };
    const base = error.error || error.message || `HTTP ${response.status}`;
    const errorMessage = error.hint ? `${base} ${error.hint}` : base;
    throw new Error(errorMessage);
  }

  return response.json();
}

// ==================== Public Catalogue API ====================

// Get public statistics (no auth required)
export async function getPublicStats(): Promise<ApiResponse<PublicStats>> {
  return fetchApi('/public/stats');
}

// Get public members list (no auth required)
export async function getPublicMembers(options?: {
  search?: string;
  superteamId?: string;
  countryCode?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<PublicMembersResponse>> {
  const params = new URLSearchParams();
  if (options?.search) params.set('search', options.search);
  if (options?.superteamId) params.set('superteamId', options.superteamId);
  if (options?.countryCode) params.set('countryCode', options.countryCode);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  
  const query = params.toString();
  return fetchApi(`/public/members${query ? `?${query}` : ''}`);
}

// Get public teams list (no auth required)
export async function getPublicTeams(options?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<PublicTeamsResponse>> {
  const params = new URLSearchParams();
  if (options?.search) params.set('search', options.search);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  
  const query = params.toString();
  return fetchApi(`/public/teams${query ? `?${query}` : ''}`);
}

// Get public projects list (no auth required)
export async function getPublicProjects(options?: {
  search?: string;
  teamId?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<PublicProjectsResponse>> {
  const params = new URLSearchParams();
  if (options?.search) params.set('search', options.search);
  if (options?.teamId) params.set('teamId', options.teamId);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  
  const query = params.toString();
  return fetchApi(`/public/projects${query ? `?${query}` : ''}`);
}

// Get CSV export URL (for direct download)
export function getExportUrl(
  type: 'members' | 'teams' | 'projects',
  options?: {
    search?: string;
    superteamId?: string;
    countryCode?: string;
    teamId?: string;
  }
): string {
  const params = new URLSearchParams();
  if (options?.search) params.set('search', options.search);
  if (options?.superteamId) params.set('superteamId', options.superteamId);
  if (options?.countryCode) params.set('countryCode', options.countryCode);
  if (options?.teamId) params.set('teamId', options.teamId);
  
  const query = params.toString();
  const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '');
  return `${baseUrl}/api/public/export/${type}.csv${query ? `?${query}` : ''}`;
}

// ==================== Achievements ====================

export async function getAchievements(userId?: string): Promise<ApiResponse<AchievementsResponse>> {
  const query = userId ? `?userId=${userId}` : '';
  return fetchApi(`/achievements${query}`);
}

export async function createAchievement(data: {
  type: AchievementType;
  title: string;
  description?: string;
  date?: string;
  issuer?: string;
  proofUrl?: string;
  superteamId?: string;
}): Promise<ApiResponse<{ achievement: Achievement }>> {
  return fetchApi('/achievements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAchievement(
  id: string,
  data: {
    type?: AchievementType;
    title?: string;
    description?: string;
    date?: string;
    issuer?: string;
    proofUrl?: string;
    superteamId?: string;
  }
): Promise<ApiResponse<{ achievement: Achievement }>> {
  return fetchApi(`/achievements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAchievement(id: string): Promise<ApiResponse<void>> {
  return fetchApi(`/achievements/${id}`, {
    method: 'DELETE',
  });
}

// ==================== OAuth Verification ====================

export async function getTeamVerificationUrl(teamId: string): Promise<ApiResponse<{ url: string; state: string }>> {
  void teamId;
  throw new Error('Teams are removed.');
}

export async function verifyTeamX(teamId: string, code: string, state: string): Promise<ApiResponse<{ team: BuilderTeam }>> {
  void teamId;
  void code;
  void state;
  throw new Error('Teams are removed.');
}

export async function getProjectVerificationUrl(projectId: string): Promise<ApiResponse<{ url: string; state: string }>> {
  return fetchApi(`/projects/${projectId}/verify-x/url`);
}

export async function verifyProjectX(projectId: string, code: string, state: string): Promise<ApiResponse<{ project: Project }>> {
  return fetchApi(`/projects/${projectId}/verify-x/callback`, {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  });
}

// ==================== Project Likes ====================

export async function likeProject(projectId: string): Promise<ApiResponse<{ likeCount: number; isLiked: boolean }>> {
  return fetchApi(`/projects/${projectId}/like`, {
    method: 'POST',
  });
}

export async function getProjectLikes(projectId: string): Promise<ApiResponse<{ likeCount: number; isLiked?: boolean }>> {
  return fetchApi(`/projects/${projectId}/likes`);
}

// ==================== Leaderboard ====================

export async function getSuperteamLeaderboard(): Promise<ApiResponse<SuperteamLeaderboardResponse>> {
  return fetchApi('/leaderboard/superteams');
}

export async function getCountryLeaderboard(): Promise<ApiResponse<CountryLeaderboardResponse>> {
  return fetchApi('/leaderboard/countries');
}

export async function getGlobalLeaderboard(): Promise<ApiResponse<GlobalLeaderboardResponse>> {
  return fetchApi('/leaderboard/global');
}

// ==================== Events ====================

export interface LumaEvent {
  id: string;
  title: string;
  description?: string;
  coverImage?: string;
  startAt?: string;
  endAt?: string;
  timezone?: string;
  date?: string;
  location?: string;
  locationType?: 'virtual' | 'in-person' | 'hybrid';
  url: string;
  organizer?: string;
  rsvpCount?: number;
  category?: string;
  superteam?: string;
  tags?: string[];
}

export interface EventsResponse {
  success: boolean;
  events: LumaEvent[];
  cached?: boolean;
}

export async function getEvents(options?: {
  tag?: string;
  includePast?: boolean;
  limit?: number;
}): Promise<EventsResponse> {
  const params = new URLSearchParams();
  if (options?.tag) params.set('tag', options.tag);
  if (options?.includePast) params.set('includePast', 'true');
  if (options?.limit) params.set('limit', String(options.limit));

  const query = params.toString();
  return fetchApi<EventsResponse>(`/events${query ? `?${query}` : ''}`);
}